// src/components/ProductItem.jsx
import React, { useEffect, useState } from "react";
import { IoClose, IoDuplicateSharp } from "react-icons/io5";
import { Md360, MdHeight } from "react-icons/md";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import placeholder from "../assets/placeholder.jpg";

const ProductItem = ({
  pn,
  selectProductNodeInstance,
  removeProductNodeInstance,
  duplicateProductNode,
  rotateProductNodeInstance,
  changeProductNodeInstanceHeight,
  applyMaterial,
  labels,
  isExpanded,
  onToggle,
}) => {
  const [materialsOpen, setMaterialsOpen] = useState(true); // Default to open if expanded
  const [activeTab, setActiveTab] = useState("");

  const productName =
    pn.matchedLocalProduct?.name || pn.refData?.name || "Prodotto";

  // Get available material types dynamically from configurableMaterials
  const availableMaterialTypes =
    pn.configurableMaterials
      ?.filter(
        (mat) => mat.materialVariations && mat.materialVariations.length > 1
      )
      .map((mat) => ({
        id: mat.name,
        label:
          mat.displayLabel ||
          mat.name.charAt(0).toUpperCase() + mat.name.slice(1),
      })) || [];

  useEffect(() => {
    // Determine active tab - prefer "imbottito" or first available
    if (availableMaterialTypes.length > 0) {
      if (!activeTab) {
        const imbottito = availableMaterialTypes.find((t) =>
          t.id.toLowerCase().includes("imbottito")
        );
        setActiveTab(imbottito ? imbottito.id : availableMaterialTypes[0].id);
      }
    }
  }, [availableMaterialTypes, activeTab]);

  const getAppliedMaterial = (materialType) => {
    if (!pn.configurableMaterials) return null;

    const material = pn.configurableMaterials.find(
      (m) => m.name === materialType
    );

    if (!material) return null;

    const variationName = material.appliedVariation || material.activeVariation;
    if (!variationName) return null;

    const variation = material.materialVariations?.find(
      (v) => v.name === variationName
    );

    if (variation) {
      return {
        name: variation.displayName || variation.name,
        thumbnail: variation.thumbnail || placeholder,
        color: variation.color,
      };
    }

    return {
      name: variationName,
      thumbnail: placeholder,
    };
  };

  const handleRotationChange = (delta) => {
    const currentDeg = Math.round(((pn.rotationAngle || 0) * 180) / Math.PI);
    let newDeg = currentDeg + delta;
    if (newDeg >= 360) newDeg -= 360;
    if (newDeg < 0) newDeg += 360;

    // Mock event for handler
    rotateProductNodeInstance(pn)({
      target: { value: newDeg },
      stopPropagation: () => {},
      preventDefault: () => {},
    });
  };

  const handleHeightChange = (delta) => {
    // Work with the actual height value (decimal), not the percentage integer
    const currentHeight = pn.height || 1;
    const deltaAsHeight = delta / 100; // Convert percentage delta to height scale
    const newHeight =
      Math.round((currentHeight + deltaAsHeight) * 10000) / 10000; // Round to avoid float errors

    // Mock event for handler
    changeProductNodeInstanceHeight(pn)({
      target: { value: newHeight * 100 }, // Convert back to percentage scale
      stopPropagation: () => {},
      preventDefault: () => {},
    });
  };

  const MaterialThumbnail = ({ material, label }) => {
    if (!material) return null;

    return (
      <div className="ay-flex ay-items-center ay-gap-2 ay-mb-2">
        <div className="ay-w-10 ay-h-10 ay-rounded ay-border ay-border-gray-300 ay-overflow-hidden ay-flex-shrink-0">
          {material.thumbnail && material.thumbnail.startsWith("#") ? (
            <div
              className="ay-w-full ay-h-full"
              style={{ backgroundColor: material.thumbnail }}
            />
          ) : (
            <img
              src={material.thumbnail}
              alt={material.name}
              className="ay-w-full ay-h-full ay-object-cover"
              onError={(e) => {
                e.target.src = placeholder;
              }}
            />
          )}
        </div>
        <div className="ay-flex-1 ay-min-w-0">
          {/* Only show label if multiple types exist, or generic "Materiale" */}
          {label && <div className="ay-text-xs ay-text-gray-500">{label}</div>}
          <div
            className="ay-text-sm ay-font-medium ay-truncate"
            title={material.name}
          >
            {material.name}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`
        ay-border ay-rounded-lg ay-bg-white ay-shadow-sm ay-overflow-hidden ay-transition-all ay-duration-300
        ${pn.colliding ? "ay-border-red-500" : "ay-border-gray-200"}
        ${isExpanded ? "ay-ring-1 ay-ring-blue-500" : ""}
      `}
    >
      {/* Product Header (Always visible) */}
      <div
        className="ay-flex ay-items-center ay-p-3 ay-cursor-pointer ay-hover:ay-bg-gray-50"
        onClick={onToggle}
      >
        <img
          src={pn.refData?.previewPath || placeholder}
          alt={productName}
          className="ay-w-12 ay-h-12 ay-object-cover ay-rounded ay-flex-shrink-0 ay-mr-3 ay-bg-gray-100"
          onError={(e) => {
            e.target.src = placeholder;
          }}
        />

        <div className="ay-flex-1 ay-min-w-0 ay-mr-2">
          <h3
            className="ay-font-bold ay-text-sm ay-truncate"
            title={productName}
          >
            {productName}
          </h3>
          <p className="ay-text-xs ay-text-gray-500 ay-truncate">
            {pn.projectCode}
          </p>
        </div>

        {/* Actions (always visible or only on hover/expand? keep visible for quick access) */}
        {!isExpanded && (
          <div className="ay-flex ay-gap-1">
            <button
              onClick={duplicateProductNode(pn)}
              className="ay-p-1.5 ay-text-gray-400 ay-hover:ay-text-blue-600 ay-hover:ay-bg-blue-50 ay-rounded"
              title="Duplica"
            >
              <IoDuplicateSharp size={16} />
            </button>
            <button
              onClick={removeProductNodeInstance(pn)}
              className="ay-p-1.5 ay-text-gray-400 ay-hover:ay-text-red-600 ay-hover:ay-bg-red-50 ay-rounded"
              title="Rimuovi"
            >
              <IoClose size={18} />
            </button>
          </div>
        )}

        <div
          className={`ay-ml-2 ay-text-gray-400 ay-transition-transform ${
            isExpanded ? "ay-rotate-180" : ""
          }`}
        >
          <FaChevronDown />
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="ay-p-4 ay-border-t ay-border-gray-100 ay-bg-gray-100">
          {/* Top Actions for Expanded View */}
          <div className="ay-flex ay-justify-end ay-gap-2 ay-mb-4">
            <button
              onClick={duplicateProductNode(pn)}
              className="ay-flex ay-items-center ay-gap-1 ay-px-3 ay-py-1.5 ay-text-xs ay-font-medium ay-text-blue-700 ay-bg-blue-100 ay-rounded hover:ay-bg-blue-200 ay-transition"
            >
              <IoDuplicateSharp size={14} /> Duplica
            </button>
            <button
              onClick={removeProductNodeInstance(pn)}
              className="ay-flex ay-items-center ay-gap-1 ay-px-3 ay-py-1.5 ay-text-xs ay-font-medium ay-text-red-700 ay-bg-red-100 ay-rounded hover:ay-bg-red-200 ay-transition"
            >
              <IoClose size={16} /> Rimuovi
            </button>
          </div>

          {/* Rotation Control - Buttons */}
          <div className="ay-mb-4 ay-bg-white ay-p-3 ay-rounded ay-border ay-border-gray-200">
            <label className="ay-flex ay-items-center ay-gap-2 ay-text-xs ay-font-bold ay-text-gray-700 ay-mb-2">
              <Md360 size={16} />
              ROTAZIONE
            </label>
            <div className="ay-flex ay-items-center ay-justify-between ay-gap-3">
              <button
                onClick={() => handleRotationChange(-45)}
                className="ay-w-8 ay-h-8 ay-flex ay-items-center ay-justify-center ay-bg-gray-100 ay-hover:ay-bg-gray-200 ay-rounded ay-text-gray-700 ay-font-bold"
              >
                -45°
              </button>
              <button
                onClick={() => handleRotationChange(-5)}
                className="ay-w-8 ay-h-8 ay-flex ay-items-center ay-justify-center ay-bg-gray-100 ay-hover:ay-bg-gray-200 ay-rounded ay-text-gray-700"
              >
                <FaChevronDown className="ay-rotate-90" size={10} />
              </button>

              <span className="ay-flex-1 ay-text-center ay-font-mono ay-text-sm ay-font-semibold">
                {Math.round(((pn.rotationAngle || 0) * 180) / Math.PI)}°
              </span>

              <button
                onClick={() => handleRotationChange(5)}
                className="ay-w-8 ay-h-8 ay-flex ay-items-center ay-justify-center ay-bg-gray-100 ay-hover:ay-bg-gray-200 ay-rounded ay-text-gray-700"
              >
                <FaChevronUp className="ay-rotate-90" size={10} />
              </button>
              <button
                onClick={() => handleRotationChange(45)}
                className="ay-w-8 ay-h-8 ay-flex ay-items-center ay-justify-center ay-bg-gray-100 ay-hover:ay-bg-gray-200 ay-rounded ay-text-gray-700 ay-font-bold"
              >
                +45°
              </button>
            </div>
          </div>

          {/* Height Control - Buttons */}
          <div className="ay-mb-4 ay-bg-white ay-p-3 ay-rounded ay-border ay-border-gray-200">
            <label className="ay-flex ay-items-center ay-gap-2 ay-text-xs ay-font-bold ay-text-gray-700 ay-mb-2">
              <MdHeight size={16} />
              ALTEZZA
            </label>
            <div className="ay-flex ay-items-center ay-justify-between ay-gap-3">
              <button
                onClick={() => handleHeightChange(-10)}
                className="ay-w-8 ay-h-8 ay-flex ay-items-center ay-justify-center ay-bg-gray-100 ay-hover:ay-bg-gray-200 ay-rounded ay-text-gray-700 ay-font-bold"
              >
                -10
              </button>
              <button
                onClick={() => handleHeightChange(-0.25)}
                className="ay-w-8 ay-h-8 ay-flex ay-items-center ay-justify-center ay-bg-gray-100 ay-hover:ay-bg-gray-200 ay-rounded ay-text-gray-700 ay-font-bold"
              >
                -0.25
              </button>

              <span className="ay-flex-1 ay-text-center ay-font-mono ay-text-sm ay-font-semibold">
                {Math.round(((pn.height || 1) - 1) * 100)}%
              </span>

              <button
                onClick={() => handleHeightChange(+0.25)}
                className="ay-w-8 ay-h-8 ay-flex ay-items-center ay-justify-center ay-bg-gray-100 ay-hover:ay-bg-gray-200 ay-rounded ay-text-gray-700 ay-font-bold"
              >
                +0.25
              </button>
              <button
                onClick={() => handleHeightChange(+10)}
                className="ay-w-8 ay-h-8 ay-flex ay-items-center ay-justify-center ay-bg-gray-100 ay-hover:ay-bg-gray-200 ay-rounded ay-text-gray-700 ay-font-bold"
              >
                +10
              </button>
            </div>
          </div>

          {/* Materials Section */}
          {availableMaterialTypes.length > 0 && (
            <div className="ay-bg-white ay-rounded ay-border ay-border-gray-200 ay-overflow-hidden">
              <div
                className="ay-flex ay-items-center ay-justify-between ay-px-3 ay-py-2 ay-bg-gray-50 ay-border-b ay-border-gray-200 ay-cursor-pointer"
                onClick={() => setMaterialsOpen(!materialsOpen)}
              >
                <span className="ay-text-xs ay-font-bold ay-text-gray-700 ay-uppercase">
                  {labels.materials || "Materiali"}
                </span>
                {materialsOpen ? (
                  <FaChevronUp size={12} />
                ) : (
                  <FaChevronDown size={12} />
                )}
              </div>

              {materialsOpen && (
                <div className="ay-p-3 ay-bg-gray-100">
                  {/* Tabs */}
                  {availableMaterialTypes.length > 1 && (
                    <div className="ay-flex ay-gap-1 ay-mb-3 ay-overflow-x-auto ay-pb-2">
                      {availableMaterialTypes.map((type) => (
                        <button
                          key={type.id}
                          onClick={() => setActiveTab(type.id)}
                          className={`
                            ay-px-3 ay-py-1.5 ay-rounded-full border-gray-200 ay-whitespace-nowrap ay-text-xs ay-font-medium
                            ay-transition
                            ${
                              activeTab === type.id
                                ? "ay-bg-gray-800 ay-text-white"
                                : "ay-bg-gray-100 ay-text-gray-600 ay-hover:ay-bg-gray-200"
                            }
                          `}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Material Grid */}
                  {pn.configurableMaterials
                    ?.filter((mat) => mat.name === activeTab)
                    .map((configurableMat) => (
                      <div key={configurableMat.name}>
                        <div className="ay-grid ay-grid-cols-5 ay-gap-2">
                          {configurableMat.materialVariations.map(
                            (variation) => {
                              const isApplied =
                                configurableMat.appliedVariation ===
                                  variation.name ||
                                configurableMat.activeVariation ===
                                  variation.name;

                              // Get the thumbnail - check multiple properties
                              const thumbnail =
                                variation.thumbnail ||
                                variation.color ||
                                placeholder;
                              const isColorSwatch =
                                thumbnail && thumbnail.startsWith("#");

                              return (
                                <div
                                  key={variation.name}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    applyMaterial(
                                      pn,
                                      configurableMat.name,
                                      variation
                                    );
                                  }}
                                  className={`
                                  ay-cursor-pointer ay-rounded-full ay-overflow-hidden ay-relative
                                  ay-transition-all ay-duration-200
                                  ${
                                    isApplied
                                      ? "ay-ring-2 ay-ring-blue-500 ay-ring-offset-1"
                                      : "ay-hover:ay-opacity-90"
                                  }
                                `}
                                  title={
                                    variation.displayName || variation.name
                                  }
                                >
                                  {/* Thumbnail */}
                                  <div className="ay-w-full ay-aspect-square">
                                    {isColorSwatch ? (
                                      <div
                                        className="ay-w-full ay-h-full"
                                        style={{ backgroundColor: thumbnail }}
                                      />
                                    ) : (
                                      <img
                                        src={thumbnail}
                                        alt={variation.name}
                                        className="ay-w-full ay-h-full ay-object-cover"
                                        onError={(e) => {
                                          e.target.src = placeholder;
                                        }}
                                      />
                                    )}
                                  </div>
                                  <div className="ay-opacity-0 hover:ay-opacity-100 ay-absolute ay-inset-0 ay-bg-black/50 ay-flex ay-items-center ay-justify-center ay-transition-opacity">
                                    <span className="ay-text-white ay-text-[10px] ay-text-center ay-px-1 ay-line-clamp-2">
                                      {variation.displayName || variation.name}
                                    </span>
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductItem;
